export type TInboxDefaultView = 'main' | 'all'

export const defaultViewToActiveInboxTab = (view: TInboxDefaultView): 'mainInbox' | 'allMessages' =>
    view === 'all' ? 'allMessages' : 'mainInbox'

/** @deprecated Use defaultViewToActiveInboxTab */
export const defaultViewToActiveInboxLabel = defaultViewToActiveInboxTab

export interface IInboxConversationTagDefault {
    key: string
    defaultValue: boolean
}

export interface IInboxConversationTagSetting extends IInboxConversationTagDefault {
    title: string
    description: string
}

/** @deprecated Use IInboxConversationTagDefault */
export type IInboxLabelDefault = IInboxConversationTagDefault

/** @deprecated Use IInboxConversationTagSetting */
export type IInboxLabelSetting = IInboxConversationTagSetting

export interface IInboxSettingsState {
    defaultView: TInboxDefaultView
    conversationTags: Record<string, boolean>
}

const INBOX_SETTINGS_STORAGE_KEY = 'inbox_settings'
const LEGACY_DEFAULT_VIEW_KEY = 'inbox_default_view'

const buildDefaultState = (tagDefaults: IInboxConversationTagDefault[]): IInboxSettingsState => {
    const conversationTags = tagDefaults.reduce<Record<string, boolean>>((acc, item) => {
        acc[item.key] = item.defaultValue
        return acc
    }, {})

    return {
        defaultView: 'main',
        conversationTags,
    }
}

const LEGACY_INBOX_LABEL_SETTING_KEYS: Record<string, string> = {
    inbox_label_sales_navigator: 'inbox_conversation_tag_sales_navigator',
    inbox_label_new_connection: 'inbox_conversation_tag_new_connection',
    inbox_label_snoozed: 'inbox_conversation_tag_snoozed',
    inbox_label_awaiting_reply: 'inbox_conversation_tag_awaiting_reply',
    inbox_label_to_respond: 'inbox_conversation_tag_to_respond',
    inbox_label_warm_intro: 'inbox_conversation_tag_warm_intro',
    inbox_label_fyi: 'inbox_conversation_tag_fyi',
    inbox_label_actioned: 'inbox_conversation_tag_actioned',
    inbox_label_marketing: 'inbox_conversation_tag_marketing',
}

const mergeStoredConversationTags = (
    tagDefaults: IInboxConversationTagDefault[],
    stored: Record<string, boolean> | undefined,
): Record<string, boolean> => {
    const merged = buildDefaultState(tagDefaults).conversationTags
    if (!stored || typeof stored !== 'object') return merged

    Object.entries(stored).forEach(([key, value]) => {
        if (typeof value !== 'boolean') return
        const resolvedKey = LEGACY_INBOX_LABEL_SETTING_KEYS[key] ?? key
        if (resolvedKey in merged) {
            merged[resolvedKey] = value
        }
    })
    return merged
}

export const loadInboxSettings = (tagDefaults: IInboxConversationTagDefault[]): IInboxSettingsState => {
    const defaults = buildDefaultState(tagDefaults)
    const raw = window.localStorage.getItem(INBOX_SETTINGS_STORAGE_KEY)

    if (!raw) {
        const migratedState: IInboxSettingsState = {
            defaultView: window.localStorage.getItem(LEGACY_DEFAULT_VIEW_KEY) === 'all' ? 'all' : 'main',
            conversationTags: { ...defaults.conversationTags },
        }

        let hasLegacyValue = false
        tagDefaults.forEach((item) => {
            const legacyValue = window.localStorage.getItem(item.key)
            if (legacyValue === 'true' || legacyValue === 'false') {
                migratedState.conversationTags[item.key] = legacyValue === 'true'
                hasLegacyValue = true
            }
        })

        if (hasLegacyValue || window.localStorage.getItem(LEGACY_DEFAULT_VIEW_KEY)) {
            saveInboxSettings(migratedState)
            return migratedState
        }

        saveInboxSettings(defaults)
        return defaults
    }

    try {
        const parsed = JSON.parse(raw)
        const storedTags = parsed?.conversationTags ?? parsed?.labels ?? {}
        const mergedTags = mergeStoredConversationTags(tagDefaults, storedTags)

        return {
            defaultView: parsed?.defaultView === 'all' ? 'all' : 'main',
            conversationTags: mergedTags,
        }
    } catch {
        return defaults
    }
}

export const saveInboxSettings = (state: IInboxSettingsState): void => {
    window.localStorage.setItem(INBOX_SETTINGS_STORAGE_KEY, JSON.stringify(state))
}

export const resetInboxSettings = (tagDefaults: IInboxConversationTagDefault[]): IInboxSettingsState => {
    const defaults = buildDefaultState(tagDefaults)
    saveInboxSettings(defaults)
    return defaults
}
